import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Alert, CircularProgress, Chip, IconButton, Divider,
} from '@mui/material';
import { Close, EventRepeat, ArrowForward, CheckCircleOutline } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { bookingStudioOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import SessionSeatPicker, { ChosenSeat } from './seat-layout/SessionSeatPicker';

/**
 * Staff-initiated reschedule.
 *
 * The backend only offers same-class sessions inside a 30-day window that still
 * have a free slot, aren't on a studio holiday, and don't clash with the
 * member's one-per-day limit — so this dialog deliberately does NOT let staff
 * type a date. Everything listed is already known to be bookable; picking one
 * cannot fail on any of those grounds.
 *
 * ── SEATED CLASSES ──────────────────────────────────────────────────────────
 * A class that runs in a room (Chakra's bikes) needs a second decision: WHICH
 * machine. Reschedule used to make that decision silently on the server, so
 * nobody saw the room or who was already in it — and when the room was full the
 * move completed with no machine at all, putting a member on the roster with
 * nowhere to sit.
 *
 * So a seated session opens the room below the list, drawn with its live
 * bookings, and the confirm button stays disabled until a bike is chosen. The
 * server refuses an unusable pick and the unique index on (session, seatId)
 * arbitrates the race, so the same machine can never reach two people.
 *
 * Classes with no room are untouched: the picker renders nothing and confirm
 * enables as soon as a session is picked, exactly as it always did.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  booking: any | null;
}

interface Option {
  _id: string;
  date: string;
  className: string;
  /** Bikes free for a seated class, headcount otherwise — the server clamps it. */
  availableSlots: number;
  slots: number;
  /**
   * True when this class runs in a room and a bike must be chosen. NEW field —
   * absent from a backend that predates the picker, which is why `seatLayout`
   * below is the fallback rather than an assumption of "seated".
   */
  hasSeatMap?: boolean;
  seatsFree?: number | null;
  seatLayoutName?: string | null;
  /**
   * The room id, straight off the session document. Present on EVERY backend
   * that has ever had seats (schema default null), so it is the reliable
   * answer to "does this class use bikes at all" during a deploy where the API
   * is older than this panel.
   */
  seatLayout?: string | null;
}

/**
 * Does this class run in a room?
 *
 * `hasSeatMap` is what the server computes, but it is NEW and an older API does
 * not send it. `seatLayout` is the raw session field and has been present since
 * seats existed, so it is the fallback. Reading only `hasSeatMap` would leave
 * this "unknown" for every studio during a deploy where the API is older than
 * this panel — mounting the picker and flashing "Choose a bike" at 66 studios
 * that have never owned a bike. Chakra is the only studio with a room.
 */
const sessionRoomVerdict = (
  session: { hasSeatMap?: boolean; seatLayout?: string | null } | null,
): boolean | null => {
  if (!session) return null;
  if (typeof session.hasSeatMap === 'boolean') return session.hasSeatMap;
  return !!session.seatLayout;
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

const RescheduleBookingDialog: React.FC<Props> = ({ open, onClose, onSuccess, booking }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [picked, setPicked] = useState<string>('');
  const [seat, setSeat] = useState<ChosenSeat | null>(null);
  /**
   * Whether the CHOSEN session has a room, as reported by the picker after it
   * loads the map. Starts null = "not known yet", which is deliberately not the
   * same as false: confirming while this is unknown could move a member into a
   * room with no bike, so the button waits.
   */
  const [targetHasRoom, setTargetHasRoom] = useState<boolean | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!booking?._id) return;
    setLoading(true); setError(null); setPicked(''); setSeat(null);
    setTargetHasRoom(null); setDone(false);
    try {
      const res = await apiService.getRescheduleOptions(booking._id);
      setOptions(res.sessions || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load available sessions');
      setOptions([]);
    } finally { setLoading(false); }
  }, [booking]);

  useEffect(() => { if (open) load(); }, [open, load]);

  /** Changing the target session invalidates any bike chosen in the old room. */
  const pickSession = (sessionId: string) => {
    if (sessionId === picked) return;
    setPicked(sessionId);
    setSeat(null);
    // The option list already knows; the picker confirms it once the map loads.
    // Trusting the list here means the button does not flicker disabled.
    setTargetHasRoom(sessionRoomVerdict(options.find((o) => o._id === sessionId) ?? null));
    setError(null);
  };

  const pickedOption = options.find((o) => o._id === picked) ?? null;
  // A seated class must carry a bike. Unknown counts as seated — see the note
  // on `targetHasRoom`.
  const needsSeat = targetHasRoom !== false;
  const canConfirm = !!picked && !saving && (!needsSeat || !!seat);

  const confirm = async () => {
    if (!canConfirm || !booking?._id) return;
    setSaving(true); setError(null);
    try {
      // Pinned to the booking's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.rescheduleBooking(booking._id, picked, seat?.seatId, bookingStudioOf(booking));
      setDone(true);
      onSuccess();
    } catch (e: any) {
      // A lost-race message ("Bike 4 was just taken…") is only actionable if the
      // map refreshes under it, so drop the stale pick and let them choose again.
      const msg =
        e?.response?.data?.message || e?.message || 'Reschedule failed';
      if (/just taken|already taken|fully booked/i.test(String(msg))) {
        setSeat(null);
      }
      setError(msg);
    } finally { setSaving(false); }
  };

  const currentSession = booking?.session;
  const currentLabel = currentSession?.date ? fmt(currentSession.date) : '—';

  return (
    // Widens once a room is on screen: a 15-bike row centre-aligned in `sm`
    // shrinks its tiles to the point where the numbers stop being readable,
    // and the room's shape is the whole reason the map is here.
    <Dialog open={open} onClose={onClose} maxWidth={targetHasRoom ? 'md' : 'sm'} fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
        <EventRepeat sx={{ color: colors.primary }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Reschedule booking</Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
            {booking?.user?.name || 'Member'} · {currentSession?.className || booking?.className || 'Session'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {done ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleOutline sx={{ fontSize: 52, color: colors.success }} />
            <Typography sx={{ fontWeight: 700, mt: 1 }}>Booking moved</Typography>
            <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.5 }}>
              The member has been notified by push and email.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, backgroundColor: colors.backgroundTertiary }}>
                <Typography sx={{ fontSize: 10.5, color: colors.textSecondary }}>CURRENTLY</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{currentLabel}</Typography>
              </Box>
              <ArrowForward sx={{ color: colors.primary }} />
              <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, border: `1px dashed ${picked ? colors.primary : colors.divider}` }}>
                <Typography sx={{ fontSize: 10.5, color: colors.textSecondary }}>MOVING TO</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: picked ? colors.primary : colors.textSecondary }}>
                  {pickedOption ? fmt(pickedOption.date) : 'Pick a session'}
                </Typography>
                {seat && (
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.success }}>
                    Bike {seat.number ?? seat.seatId} · row {seat.row}
                  </Typography>
                )}
              </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : options.length === 0 ? (
              <Alert severity="info">
                No other <strong>{currentSession?.className || 'same-class'}</strong> session in the next 30 days
                has a free slot. Only sessions of the same class are offered, so the member keeps what they paid for.
              </Alert>
            ) : (
              <>
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 1 }}>
                  {options.length} available session{options.length === 1 ? '' : 's'} · same class · next 30 days
                </Typography>
                <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                  {options.map((o) => {
                    const on = picked === o._id;
                    return (
                      <Box key={o._id} onClick={() => pickSession(o._id)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.1, mb: 0.75,
                          borderRadius: 2, cursor: 'pointer',
                          border: `1px solid ${on ? colors.primary : colors.divider}`,
                          backgroundColor: on ? `${colors.primary}0F` : 'transparent',
                        }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: on ? 700 : 500, flex: 1 }}>
                          {fmt(o.date)}
                        </Typography>
                        <Chip
                          size="small"
                          // A seated class counts BIKES, not headcount — `slots`
                          // is a number typed into admin and can exceed what the
                          // room physically holds, which is how a "12 left"
                          // session with no free machine used to be offered.
                          label={
                            o.hasSeatMap
                              ? `${o.seatsFree} bike${o.seatsFree === 1 ? '' : 's'} free`
                              : `${o.availableSlots} of ${o.slots} left`
                          }
                          sx={{
                            height: 21, fontSize: 10.5,
                            backgroundColor: o.availableSlots <= 1 ? `${colors.warning}22` : `${colors.success}1A`,
                            color: o.availableSlots <= 1 ? '#8a5a00' : '#1b5e20',
                          }} />
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}

            {/* The target room.
                `targetHasRoom !== false` — not just `picked` — so a class the
                options list has ALREADY told us has no room never mounts this
                at all: no wasted request, and no way for a network blip on the
                seat-map call to flip `targetHasRoom` back to true and strand a
                roomless reschedule behind a "Choose a bike" button that can
                never be satisfied. `null` (an older backend that omits the
                field) still mounts, and the picker resolves it. */}
            {picked && targetHasRoom !== false && (
              <>
                <Divider sx={{ my: 2 }} />
                <SessionSeatPicker
                  sessionId={picked}
                  value={seat}
                  onChange={setSeat}
                  onRoomResolved={setTargetHasRoom}
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 11.5, color: colors.textSecondary }}>
              No new charge — the original payment moves with the booking. The member gets a push
              and email in the app they booked through. Their own one-time reschedule stays unused.
              {targetHasRoom
                ? ' Their old bike is released and the one you choose here is claimed in the new class.'
                : ''}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {done ? (
          <Button variant="contained" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!canConfirm} onClick={confirm}>
              {saving ? (
                <CircularProgress size={18} />
              ) : picked && needsSeat && !seat ? (
                'Choose a bike'
              ) : (
                'Confirm reschedule'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleBookingDialog;
